import type { Company } from "@repo/app-types/entities/company";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import { getInterviewLinkForProject } from "@repo/app-types/entities/project";
import { splitProjectQuestions } from "@repo/app-types/helpers/utilities";
import { Label } from "@repo/ui/Label/label";
import { Size } from "@repo/ui/helpers/helpers";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { ProjectSettingsQuestionsSection } from "../surveyQuestions/projectSettingsQuestionSection.js";
import { AIContextSetting } from "./aiContextSetting.js";
import { AppearanceSettingsSection } from "./appearanceSettingsSection.js";
import { InterviewLinkCopybox } from "./interviewLinkCopybox.js";
import { PhoneNumberDisplaySection } from "./phoneNumberDisplaySection.js";
import { ProjectNotificationsSettingsSection } from "./projectNotificationsSettingsSection.js";

interface VoiceModeProjectSettingsProps {
  project: ProjectWithInterviewCount;
  company: Company;
  onSaveProject: (project: ProjectUpdate) => Promise<void>;
  onClickSharingTemplates: () => void;
}

export const VoiceModeProjectSettings = ({
  project,
  onSaveProject,
  company,
  onClickSharingTemplates,
}: VoiceModeProjectSettingsProps) => {
  const { basicQuestions } = splitProjectQuestions(project.questions);

  return (
    <>
      <div className="flex flex-col space-y-6">
        <div>
          <div className="rounded-lg border border-border-light bg-white px-4 py-3">
            <div className="flex flex-row space-x-4">
              <div className="shrink-0">
                <InterviewLinkCopybox
                  className="mb-2"
                  linkLabel="Interview link"
                  inviteLinkUrl={getInterviewLinkForProject(
                    import.meta.env.VITE_INTERVIEW_APP_BASE_URL,
                    company,
                    project,
                  )}
                  project={project}
                  onCopyInterviewLink={() => {
                    showToast({
                      title: `Interview link copied`,
                      variant: ToastVariant.Success,
                    });
                  }}
                />
              </div>
              <div className="shrink-0">
                <Label size={Size.SMALL}>Phone number</Label>
                <PhoneNumberDisplaySection
                  projectId={project.id}
                  company={company}
                />
              </div>
            </div>
            <div className="mb-2 text-sm text-foreground">
              Send the interview link or phone number to candidates or add it
              directly to your job posting (
              <span
                onClick={onClickSharingTemplates}
                className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
              >
                See Templates
              </span>
              ). Links work on desktop or mobile browsers and can be customized
              with a custom logo and messaging.
            </div>
          </div>
        </div>

        <div>
          <Label size={Size.LARGE}>Interview settings</Label>
          <div className="flex flex-col space-y-3">
            <div>
              <ProjectSettingsQuestionsSection
                company={company}
                project={project}
                complexQuestions={[]}
                basicQuestions={basicQuestions}
                onSaveProject={(p) => void onSaveProject(p)}
              />
            </div>

            <AIContextSetting
              context={project.settings.prompt_context}
              onSave={(newContext) => {
                void onSaveProject({
                  id: project.id,
                  settings: {
                    ...project.settings,
                    prompt_context: newContext ?? null,
                  },
                });
              }}
            />
          </div>
        </div>
      </div>
      <ProjectNotificationsSettingsSection
        project={project}
        onSaveProject={(p) => void onSaveProject(p)}
        isTrainingProject={false}
      />
      <AppearanceSettingsSection
        onSaveProject={(p) => void onSaveProject(p)}
        company={company}
        project={project}
      />
    </>
  );
};
